// ─────────────────────────────────────────────────────────────────────────────
//  ManagedAlertCard.jsx
//  Single alert card — used in BOTH Live tab and Search History tab.
//  Collapsible: summary row + expanded AlertDetail panel.
//  The map/navigate button is HIDDEN on the summary row (per requirement).
//  Location can still be accessed via "Live Track" / "View Location" inside
//  the expanded AlertDetail.
// ─────────────────────────────────────────────────────────────────────────────
import React from "react";
import { Grow } from "@material-ui/core";

import {
  ALERT_CONFIGS, ALERT_BORDER, ALERT_ICON_SRC,
  ALERT_TYPE_LABEL, notificationIcon,
  formatEventTime, getDriver,
} from "./notificationConstants";
import AlertDetail from "./AlertDetail";

const ManagedAlertCard = React.memo(({
  n,
  index,
  isExpanded,
  onToggle,
  onAction,
  onMapClick,
  classes,
  type = "VEHICLE"
}) => {
  const alertCfg = ALERT_CONFIGS[n.notificationType] || ALERT_CONFIGS.default;
  const borderColor = ALERT_BORDER[n.notificationType] || ALERT_BORDER.default;
  const isOpen = n.status === "open";
  const isRejected = n.status === "rejected";
  const driver = getDriver(n.deviceName);

  const subtitle = (() => {
    const vehicle = n.deviceName || "UNKNOWN VEHICLE";
    const time = formatEventTime(n.eventTime);
    const typeLabel = ALERT_TYPE_LABEL[n.notificationType] || n.notificationType?.replace(/_/g, " ") || "ALERT";
    if (n?.notificationType === "OVER_SPEED_ALERT") {
          return `${typeLabel} HAS BEEN DETECTED AT ${time} FOR ${type} NUMBER ${vehicle} TRAVELING AT ${n?.eventValue || 0.0} KM/H, EXCEEDING THE CONFIGURED SPEED LIMIT ${n?.configureOverSpeedValue || 0.0} KM/H.`;
    }
    return `${typeLabel} HAS BEEN DETECTED AT ${time} FOR ${type} NUMBER ${vehicle}`;
  })();

  // add near top of file (or in notificationConstants.js and import it)
  const isImageSrc = (src) =>
    typeof src === "string" &&
    (/^(data:|https?:\/\/|\/|\.\/|\.\.\/)/.test(src) ||
      /\.(svg|png|jpe?g|gif|webp)(\?.*)?$/i.test(src));

  return (
    // <Grow in timeout={100 + index * 35}>
    <div
      className={`${classes.acard} ${isExpanded ? classes.acardExpanded : ""}`}
      id={`managed-card-${n._id}`}
    >
      {/* ── Coloured left stripe ── */}
      <div className={classes.acardStripe} style={{ background: borderColor }} />

      {/* ── Summary row (click to expand/collapse) ── */}
      <div className={classes.acardSummary}
      // onClick={() => onToggle(n._id)}
      >
        {/* Alert type icon */}
        <div className={classes.acardIconWrap} style={{ background: alertCfg.bgColor, border: `1.5px solid ${borderColor}44` }}>
          {(() => {
            const iconSrc = ALERT_ICON_SRC[n.notificationType] || notificationIcon;

            if (React.isValidElement(iconSrc)) {
              return iconSrc; // our new SVG components render directly
            }
            return isImageSrc(iconSrc) ? (
              <img src={iconSrc} alt="" style={{ width: 22, height: 22, objectFit: "contain" }} />
            ) : (
              <span style={{ fontSize: 18, lineHeight: 1 }}>{iconSrc}</span>
            );
          })()}
        </div>

        {/* Middle: type label + driver + subtitle */}
        <div className={classes.acardMid}>
          <div className={classes.acardType} style={{ color: borderColor }}>
            {alertCfg.label}
            {driver.name !== "Driver N/A" && (
              <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "#64748b", marginLeft: 6 }}>
                · {driver.name}
              </span>
            )}
          </div>
          <div className={classes.acardSub} style={{ marginTop: 2 }}>
            {subtitle}
          </div>
          {/* // ADD this after <div className={classes.acardSub}>...</div> */}
          <button
            title="View on map"
            onClick={e => {
              e.stopPropagation();
              onMapClick({
                lat: n.latitude ?? 28.6139,
                lng: n.longitude ?? 77.209,
                label: `${alertCfg.label} — ${n.deviceName || ""}`,
                notificationType: n.notificationType,
                alertTime: n.eventTime,
              });
            }}
            style={{
              marginTop: 4,
              width: 24, height: 24,
              borderRadius: "50%",
              border: `1.5px solid ${n.latitude ? borderColor + "66" : "#e2e8f0"}`,
              background: n.latitude ? `${borderColor}14` : "#f1f5f9",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              cursor: n.latitude ? "pointer" : "default",
              padding: 0,
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
              stroke={n.latitude ? borderColor : "#94a3b8"} strokeWidth="2.2" strokeLinecap="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </button>
        </div>

        {/* Right: status badge + live tag */}
        {/* <div className={classes.acardMeta}>
            {isOpen && <span className={classes.badgeOpen}>● Open</span>}
            {!isOpen && !isRejected && <span className={classes.badgeClosed}>✓ Resolved</span>}
            {isRejected && <span className={classes.badgeRejected}>✕ Rejected</span>}
            {n._source === "ws" && (
              <span style={{ fontSize: "0.58rem", fontWeight: 700, background: "#082D6B", color: "#fff", borderRadius: 4, padding: "1px 5px" }}>
                ● LIVE
              </span>
            )}
          </div> */}

        {/* Chevron */}
        {/* <span className={`${classes.acardChevron} ${isExpanded ? classes.acardChevronOpen : ""}`}>▾</span> */}
      </div>

      {/* ── Expanded detail panel ── */}
      {isExpanded && (
        <AlertDetail
          n={n}
          onAction={onAction}
          onMapClick={onMapClick}
          classes={classes}
        />
      )}
    </div>
    // </Grow>
  );
});

export default ManagedAlertCard;
